import { useCallback, useEffect, useRef, useState } from 'react';
import { assertNotNil } from '../types';

export const useDragResize = <TState>({
  onResizeStart,
  onResizeProgress,
  onResizeEnd,
}: {
  // e is native DOM mouse event (not React's synthetic event)
  onResizeStart?: (
    domMouseEvent: MouseEvent,
    state: NonNullable<TState>,
  ) => void;
  // e is native DOM mouse event (not React's synthetic event)
  onResizeProgress?: (
    domMouseEvent: MouseEvent,
    state: NonNullable<TState>,
  ) => void;
  // e is native DOM mouse event (not React's synthetic event)
  onResizeEnd?: (domMouseEvent: MouseEvent, state: NonNullable<TState>) => void;
}) => {
  const resizingRef = useRef<NonNullable<TState> | undefined>(undefined);
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent<HTMLElement>, state: NonNullable<TState>) => {
      setIsResizing(true);
      resizingRef.current = state;
      onResizeStart?.(e.nativeEvent, state);
    },
    [onResizeStart],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      assertNotNil(resizingRef.current);
      onResizeProgress?.(e, resizingRef.current);
    },
    [onResizeProgress],
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      setIsResizing(false);
      assertNotNil(resizingRef.current);
      onResizeEnd?.(e, resizingRef.current);
    },
    [onResizeEnd],
  );

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [handleMouseMove, handleMouseUp, isResizing]);

  return { isResizing, handleResizeStart };
};
